import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LanguageContext";
import { TrafficLight } from "@/components/TrafficLight";
import { WarningCircleIcon, CarIcon, GaugeIcon, WrenchIcon } from "@phosphor-icons/react";
import { Link } from "react-router-dom";

export const Dashboard = () => {
  const { t, lang } = useLang();
  const [summary, setSummary] = useState(null);
  const [cars, setCars] = useState([]);

  useEffect(() => {
    (async () => {
      const [s, c] = await Promise.all([
        api.get("/dashboard/summary"),
        api.get("/cars"),
      ]);
      setSummary(s.data);
      setCars(c.data);
    })();
  }, []);

  if (!summary) return <div className="text-zinc-500 text-sm uppercase tracking-[0.3em]">Loading…</div>;

  return (
    <div className="space-y-8" data-testid="dashboard-root">
      <div>
        <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/01</div>
        <h1 className="text-4xl sm:text-5xl font-bold tracking-tight">{t("fleetOverview")}</h1>
      </div>

      {/* Stats grid */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-0 border border-white/10 bg-[#121212]">
        <StatCell icon={CarIcon} label={t("totalCars")} value={summary.total_cars} testid="stat-total-cars" />
        <StatCell icon={GaugeIcon} label={t("totalReturns")} value={summary.total_trips} testid="stat-total-trips" />
        <StatCell icon={WrenchIcon} label={t("urgent")} value={summary.status.urgent} accent="red" testid="stat-urgent" />
        <StatCell icon={WarningCircleIcon} label={t("warning")} value={summary.status.warning} accent="yellow" testid="stat-warning" last />
      </div>

      {/* Alerts */}
      <section className="border border-white/10 bg-[#121212]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/02</div>
            <h2 className="text-xl font-semibold">{t("alerts")}</h2>
          </div>
        </div>
        <div className="p-6">
          {summary.alerts.length === 0 ? (
            <div className="text-sm text-zinc-500">{t("noAlerts")}</div>
          ) : (
            <div className="space-y-2">
              {summary.alerts.map((a) => (
                <Link
                  key={a.car_id}
                  to={`/cars/${a.car_id}`}
                  data-testid={`alert-row-${a.car_id}`}
                  className="flex items-center justify-between border border-white/10 bg-[#0A0A0A] hover:bg-[#1C1C1C] px-4 py-3 transition-colors"
                >
                  <div className="flex items-center gap-4">
                    <TrafficLight status={a.level === "urgent" ? "red" : "yellow"} />
                    <div>
                      <div className="font-semibold">{a.plate_number}</div>
                      <div className="text-xs text-zinc-500 uppercase tracking-[0.2em]">
                        {a.remaining_km <= 0
                          ? `${t("over")} ${Math.abs(a.remaining_km).toFixed(0)} ${t("km")}`
                          : `${t("remaining")} ${a.remaining_km.toFixed(0)} ${t("km")}`}
                      </div>
                    </div>
                  </div>
                  <div className={`text-xs uppercase tracking-[0.2em] ${a.level === "urgent" ? "text-[#FF3B30]" : "text-[#FFEA00]"}`}>
                    {a.level === "urgent" ? t("urgent") : t("warning")}
                  </div>
                </Link>
              ))}
            </div>
          )}
        </div>
      </section>

      {/* Fleet snapshot */}
      <section className="border border-white/10 bg-[#121212]">
        <div className="flex items-center justify-between px-6 py-4 border-b border-white/10">
          <div>
            <div className="text-xs uppercase tracking-[0.3em] text-zinc-500">/03</div>
            <h2 className="text-xl font-semibold">{t("cars")}</h2>
          </div>
          <Link
            to="/cars"
            data-testid="dashboard-all-cars-link"
            className="text-xs uppercase tracking-[0.2em] text-zinc-400 hover:text-white transition-colors"
          >
            {t("all")} →
          </Link>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3">
          {cars.slice(0, 6).map((c) => {
            const kmSince = (c.current_km || 0) - (c.last_oil_change_km || 0);
            const remaining = c.oil_change_interval_km - kmSince;
            const pct = kmSince / c.oil_change_interval_km;
            const level = remaining <= 0 || pct >= 1 ? "red" : pct >= 0.8 ? "yellow" : "green";
            return (
              <Link
                key={c.car_id}
                to={`/cars/${c.car_id}`}
                data-testid={`fleet-card-${c.car_id}`}
                className="grid-cell p-6 card-hover"
              >
                <div className="flex items-center justify-between mb-4">
                  <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{c.make} {c.model}</div>
                  <TrafficLight status={level} size="sm" />
                </div>
                <div className="text-2xl font-bold">{c.plate_number}</div>
                <div className="mt-4 space-y-1 text-sm">
                  <Row label={t("currentKm")} value={`${(c.current_km || 0).toLocaleString(lang === "ar" ? "ar-EG" : "en-US")} ${t("km")}`} />
                  <Row
                    label={t("remainingKm")}
                    value={
                      remaining <= 0
                        ? `-${Math.abs(remaining).toLocaleString()} ${t("km")}`
                        : `${remaining.toLocaleString()} ${t("km")}`
                    }
                    valueClass={level === "red" ? "text-[#FF3B30]" : level === "yellow" ? "text-[#FFEA00]" : "text-[#39FF14]"}
                  />
                </div>
              </Link>
            );
          })}
        </div>
        {cars.length === 0 && (
          <div className="p-8 text-center">
            <div className="text-sm text-zinc-500 mb-4">{t("firstTimeCta")}</div>
            <Link
              to="/cars"
              data-testid="empty-add-car-link"
              className="inline-block bg-white text-black px-4 py-2 text-sm font-semibold hover:bg-zinc-100 transition-colors"
            >
              {t("addCar")}
            </Link>
          </div>
        )}
      </section>
    </div>
  );
};

const StatCell = ({ icon: Icon, label, value, accent, testid, last }) => {
  const color = accent === "red" ? "text-[#FF3B30]" : accent === "yellow" ? "text-[#FFEA00]" : "text-white";
  return (
    <div
      data-testid={testid}
      className={`p-6 flex items-center gap-4 ${last ? "" : "border-e border-white/10"} border-b md:border-b-0 border-white/10`}
    >
      <div className="p-2 border border-white/10 bg-[#0A0A0A]">
        <Icon size={22} weight="bold" className={color} />
      </div>
      <div>
        <div className="text-xs uppercase tracking-[0.2em] text-zinc-500">{label}</div>
        <div className={`text-3xl font-bold ${color}`}>{value}</div>
      </div>
    </div>
  );
};

const Row = ({ label, value, valueClass = "" }) => (
  <div className="flex items-center justify-between">
    <span className="text-zinc-500">{label}</span>
    <span className={`font-semibold ${valueClass}`}>{value}</span>
  </div>
);
